import { useIntl } from '@/core/i18n/use-intl';
import { Progress } from '@/shared/components/ui';
import { cn } from '@/shared/utils';

import { evaluatePasswordStrength } from '../utils/password-strength';

interface PasswordStrengthIndicatorProps {
  password: string;
}

const strengthColorClassByLevel: Record<'too-short' | 'weak' | 'medium' | 'strong', string> = {
  'too-short': 'text-red-600',
  weak: 'text-red-600',
  medium: 'text-yellow-600',
  strong: 'text-green-600',
};

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const { t } = useIntl();
  const strength = evaluatePasswordStrength(password);

  if (strength.level === 'empty') {
    return (
      <p className="text-muted-foreground text-xs">{t('auth.passwordStrengthRequirementsHint')}</p>
    );
  }

  const levelTextByLevel: Record<'too-short' | 'weak' | 'medium' | 'strong', string> = {
    'too-short': t('auth.passwordStrengthLevels.tooShort'),
    weak: t('auth.passwordStrengthLevels.weak'),
    medium: t('auth.passwordStrengthLevels.medium'),
    strong: t('auth.passwordStrengthLevels.strong'),
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{t('auth.passwordStrength')}</span>
        <span className={cn('font-medium', strengthColorClassByLevel[strength.level])}>
          {levelTextByLevel[strength.level]}
        </span>
      </div>
      <Progress value={strength.progress} />
      <p className="text-muted-foreground text-xs">{t('auth.passwordStrengthRequirementsHint')}</p>
    </div>
  );
}
